import React, { useState, useCallback, ReactNode } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { useTranslate } from '@pegasus/translation';
import { BrazeProvider, getLocalBrazeConfig } from '@pegasus/braze';
import { ErrorBoundary } from '@browsing/components';
import { useAutologinTracking } from '@browsing/hooks';
import { LazySuccessOverlaySelector } from '@browsing/overlays';
import { removeQueryParams } from '@browsing/utils';

import { getWebviewOs } from '@client/redux/modules/clientDevice/selectors';
import { getCustomer, getEmail, getHasEmailConfirmed } from '@client/redux/modules/customer';
import Header from '@client/components/header';
import Footer from '@client/components/footer';
import Usabilla from '@client/components/usabilla';
import LoadableGenericErrorMessage from '@client/components/shared/genericErrorMessage';
import { PreliminaryOrder } from '@client/components/shared/preliminary-order';
import { GlobalStyle } from '@client/styles/styled-components/global-styles';
import { getCookie } from '@client/helpers/cookie';
import { track } from '@client/helpers/tracking';
import { ApplicationContext } from '@client/components/mylounge/helpers';
import { useConfig } from '@browsing/config';

import { isTouchScreen } from '../helpers';

import '../styles/core.scss';

type Props = {
    children: ReactNode;
};

const CoreLayout: React.FC<Props> = ({ children }) => {
    const location = useLocation();
    const translate = useTranslate('meta');
    const isWebview = Boolean(useSelector(getWebviewOs));
    const email = useSelector(getEmail);
    const customer = useSelector(getCustomer);
    const isMailConfrimed = useSelector(getHasEmailConfirmed);

    const [appContextState, setAppContextState] = useState({
        smartbannerVisibility: false,
    });

    const updateApplicationContext = useCallback(
        isShown => {
            setAppContextState(prevContextState => {
                return {
                    ...prevContextState,
                    smartbannerVisibility: isShown,
                };
            });
        },
        [setAppContextState],
    );

    useAutologinTracking(getCookie, track);

    // This function returns the first string between the first two '/' of the router pathname
    // example /campaigns/ZZLE6D -> campaigns
    // then we add a class to the inner-wrapper campaigns-page
    const getPageClassName = () => {
        return location.pathname.split('/')[1];
    };

    const config = useConfig();
    const localeCode = config.locale[config.appdomain];
    const isBrazeAllowedByConfig = config.isEnabled('braze');
    const areBrazeCardsAllowedByConfig = config.isEnabled('brazeCard');
    const environment = window && window.CONFIG_ENV;
    const brazeConfig = getLocalBrazeConfig(isBrazeAllowedByConfig, localeCode);
    const { hashedCustomerNumber, gender, firstName } = customer;

    return (
        <BrazeProvider
            hashedCustomerNumber={hashedCustomerNumber}
            gender={gender}
            firstName={firstName}
            brazeConfig={brazeConfig}
            environment={environment}
            brazeAllowedByConfig={isBrazeAllowedByConfig}
            brazeCarrdsAllowedByConfig={areBrazeCardsAllowedByConfig}
            location="pdp"
        >
            <GlobalStyle />
            <div
                id="inner-wrapper"
                className={classNames(`${getPageClassName()}-page`, {
                    'touch-device': isTouchScreen(),
                    'not-touch-device': !isTouchScreen(),
                    'safe-area-wrapper': isWebview,
                })}
            >
                <Helmet
                    titleTemplate={`%s - ${translate('title')}`}
                    defaultTitle={translate('title')}
                    meta={[
                        {
                            name: 'description',
                            content: translate('description'),
                        },
                        {
                            name: 'keywords',
                            content: translate('keywords'),
                        },
                    ]}
                />
                <Header updateApplicationContext={updateApplicationContext} />

                <ApplicationContext.Provider value={appContextState}>
                    <ErrorBoundary
                        fallbackUI={LoadableGenericErrorMessage}
                        componentName="CoreLayout"
                    >
                        {children}
                    </ErrorBoundary>
                </ApplicationContext.Provider>

                <Usabilla />
                <Footer />

                {!isMailConfrimed && (
                    <LazySuccessOverlaySelector
                        email={email}
                        onClose={removeQueryParams}
                        queryString={location.search}
                    />
                )}

                <PreliminaryOrder />
            </div>
        </BrazeProvider>
    );
};

export default CoreLayout;
